export const coerceBoolean = (value) => {
  if (value === undefined || value === null) return value;
  if (typeof value === 'boolean') return value;
  return Number(value) === 1;
};

export const coerceNumber = (value, { allowNull = true } = {}) => {
  if (value === undefined) return undefined;
  if (value === null || value === '') return allowNull ? null : undefined;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const RALLY_API_KEYS = [
  'deeplink',
  'title',
  'description',
  'shortDescription',
  'specialIDs',
  'companyIDs',
  'isPublic',
  'requiresTicket',
  'galleryEnabled',
  'galleryRetentionMode',
  'galleryRetentionDays',
  'allowMultipleTourDealsPerVenue',
  'blacklistHideFromUser',
  'isDebugRally',
  'excludedFromCommission',
  'tags',
  'rallyCategoryID',
  'maxParticipants',
  'scheduleType',
  'scheduleConfig',
  'isHighlighted',
  'segmentationConfig',
];

const parseRallyIdList = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseRallyTags = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseSegmentationConfig = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
};

export const formatRallyForEdit = (rally = {}) => ({
  ...rally,
  specialIDs: parseRallyIdList(rally.specialIDs),
  companyIDs: parseRallyIdList(rally.companyIDs),
  tags: parseRallyTags(rally.tags),
  rallyCategoryID: rally.rallyCategoryID ?? rally.rally_category_id ?? null,
  deleted: coerceBoolean(rally.deleted),
  isPublic: coerceBoolean(rally.isPublic),
  requiresTicket: coerceBoolean(rally.requiresTicket ?? rally.requires_ticket),
  galleryEnabled: coerceBoolean(rally.galleryEnabled ?? rally.gallery_enabled),
  galleryRetentionMode: rally.galleryRetentionMode ?? rally.gallery_retention_mode ?? 'until_next_occurrence',
  galleryRetentionDays: coerceNumber(
    rally.galleryRetentionDays ?? rally.gallery_retention_days ?? 7,
    { allowNull: false },
  ),
  allowMultipleTourDealsPerVenue: coerceBoolean(
    rally.allowMultipleTourDealsPerVenue ?? rally.allow_multiple_tour_deals_per_venue,
  ),
  blacklistHideFromUser: coerceBoolean(
    rally.blacklistHideFromUser ?? rally.blacklist_hide_from_user ?? true,
  ),
  isDebugRally: coerceBoolean(rally.isDebugRally),
  excludedFromCommission: coerceBoolean(
    rally.excludedFromCommission ?? rally.excluded_from_commission,
  ),
  isHighlighted: coerceBoolean(rally.isHighlighted),
  maxParticipants: rally.maxParticipants === undefined || rally.maxParticipants === ''
    ? null
    : coerceNumber(rally.maxParticipants, { allowNull: true }),
  segmentationConfig: parseSegmentationConfig(rally.segmentationConfig ?? rally.segmentation_config),
});

export const formatRallyForApi = (rally = {}) => {
  const normalized = formatRallyForEdit(rally);
  return RALLY_API_KEYS.reduce((body, key) => {
    if (normalized[key] !== undefined) {
      body[key] = normalized[key];
    }
    return body;
  }, {});
};

export const formatUserForEdit = (user = {}) => ({
  ...user,
  isDebugUser: user.isDebugUser == null ? false : coerceBoolean(user.isDebugUser),
  debugPremium: user.debugPremium == null ? false : coerceBoolean(user.debugPremium),
  memberInviteQuotaBonus: coerceNumber(user.memberInviteQuotaBonus, { allowNull: false }) ?? 0,
  moderationStatus: user.moderationStatus || user.moderation_status || 'active',
  moderationUpdatedAt: user.moderationUpdatedAt ?? user.moderation_updated_at ?? null,
  accountClosed: Boolean(user.accountClosed),
  identityPermanentlyBlocked: Boolean(user.identityPermanentlyBlocked),
});

const USER_API_KEYS = [
  'firstName',
  'lastName',
  'displayIdentity',
  'birthday',
  'description',
  'profileMailAddress',
  'gender',
  'billingMail',
  'billingPostalCode',
  'billingCity',
  'billingStreet',
  'billingStreetNumber',
  'hobbys',
  'interests',
  'isDebugUser',
  'debugPremium',
  'memberInviteQuotaBonus',
  'phone_number',
];

const USER_STRING_KEYS = new Set([
  'firstName',
  'lastName',
  'displayIdentity',
  'description',
  'profileMailAddress',
  'gender',
  'billingMail',
  'billingPostalCode',
  'billingCity',
  'billingStreet',
  'billingStreetNumber',
  'phone_number',
]);

const USER_BOOLEAN_KEYS = new Set(['isDebugUser', 'debugPremium']);

const normalizeEmail = (value) => {
  if (value === undefined) return undefined;
  // Backend expects `nullable()` and validates email format;
  // empty string '' fails `.email()`, so use null for "unset".
  if (value === null || value === '') return null;
  if (typeof value === 'string') return value;
  return undefined;
};

const normalizeBirthday = (value) => {
  if (value === undefined) return undefined;
  if (!value || String(value).startsWith('0000')) return undefined;
  const datePart = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : undefined;
};

const normalizeHobbyRefs = (items) => (
  items
    .map((item) => coerceNumber(item.hobbyID, { allowNull: false }))
    .filter((hobbyID) => hobbyID != null && !Number.isNaN(hobbyID))
    .map((hobbyID) => ({ hobbyID }))
);

const normalizeInterestRefs = (items) => (
  items
    .map((item) => coerceNumber(item.interestID, { allowNull: false }))
    .filter((interestID) => interestID != null && !Number.isNaN(interestID))
    .map((interestID) => ({ interestID }))
);

export const formatUserForApi = (
  user = {},
  { includeDisplayIdentity = false, memberInviteQuotaBonusOverride = undefined } = {},
) => {
  const normalized = formatUserForEdit(user);
  const explicitKeys = new Set(Object.keys(user));

  const body = USER_API_KEYS.reduce((acc, key) => {
    if (!explicitKeys.has(key)) return acc;
    if (key === 'displayIdentity' && !includeDisplayIdentity) return acc;

    let value = normalized[key];
    if (value === undefined) return acc;

    if (key === 'profileMailAddress' || key === 'billingMail') {
      value = normalizeEmail(value);
      if (value === undefined) return acc;
    } else if (key === 'birthday') {
      value = normalizeBirthday(value);
      if (value === undefined) return acc;
    } else if (key === 'displayIdentity') {
      const trimmed = String(value || '').trim();
      if (!trimmed) return acc;
      value = trimmed;
    } else if (key === 'hobbys' && Array.isArray(value)) {
      value = normalizeHobbyRefs(value);
    } else if (key === 'interests' && Array.isArray(value)) {
      value = normalizeInterestRefs(value);
    } else if (key === 'memberInviteQuotaBonus') {
      value = Math.max(0, Math.floor(Number(value) || 0));
    } else if (USER_BOOLEAN_KEYS.has(key)) {
      if (typeof value !== 'boolean') return acc;
    } else if (USER_STRING_KEYS.has(key) && (value === null || typeof value !== 'string')) {
      value = value == null ? '' : String(value);
    } else if (value === null) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

  if (memberInviteQuotaBonusOverride !== undefined) {
    body.memberInviteQuotaBonus = Math.max(
      0,
      Math.floor(Number(memberInviteQuotaBonusOverride) || 0),
    );
  }

  return body;
};

export const formatEventForApi = (event = {}) => ({
  ...event,
  deleted: coerceBoolean(event.deleted),
  price: coerceNumber(event.price, { allowNull: false }),
  participantCount: coerceNumber(event.participantCount, { allowNull: true }),
  maleCount: event.maleCount === null || event.maleCount === ''
    ? null
    : coerceNumber(event.maleCount, { allowNull: true }),
  femaleCount: event.femaleCount === null || event.femaleCount === ''
    ? null
    : coerceNumber(event.femaleCount, { allowNull: true }),
  diversCount: event.diversCount === null || event.diversCount === ''
    ? null
    : coerceNumber(event.diversCount, { allowNull: true }),
  minAge: event.minAge === null || event.minAge === ''
    ? null
    : coerceNumber(event.minAge, { allowNull: true }),
  maxAge: event.maxAge === null || event.maxAge === ''
    ? null
    : coerceNumber(event.maxAge, { allowNull: true }),
  eventTagID: coerceNumber(event.eventTagID, { allowNull: false }),
  intervalTypeID: coerceNumber(event.intervalTypeID, { allowNull: false }),
});
